// Settings shell. Desktop renders a two-column layout with sidebar
// and detail pane. Mobile renders either a list of sections (when no
// `:section` param) or the section detail inside MobileWizardShell.
// The URL is the source of truth so deep links work and the back
// button returns to the section list on mobile.

import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useVault } from "../../hooks/useVault";
import { MobileWizardShell } from "../shell/MobileWizardShell";
import { VaultDetailsTab } from "./VaultDetailsTab";
import { SecurityTab } from "./SecurityTab";
import { CacheTab } from "./CacheTab";
import { MasterPasswordTab } from "./MasterPasswordTab";
import { VaultsTab } from "./VaultsTab";
import { ExportTab } from "./ExportTab";
import { PreferencesTab } from "./PreferencesTab";
import { AboutTab } from "./AboutTab";
import { MasterRotationModal } from "../lifecycle/MasterRotationModal";
import {
  changeMaster,
  type RotationProgress,
} from "../../lib/masterRotation";

type TabId =
  | "vault"
  | "security"
  | "cache"
  | "master"
  | "vaults"
  | "export"
  | "preferences"
  | "about";

const TABS: { id: TabId; label: string; group: "vault" | "app" }[] = [
  { id: "vault", label: "Vault details", group: "vault" },
  { id: "security", label: "Security", group: "vault" },
  { id: "cache", label: "Cache", group: "vault" },
  { id: "master", label: "Master password", group: "app" },
  { id: "vaults", label: "Vaults", group: "app" },
  { id: "export", label: "Export backup", group: "app" },
  { id: "preferences", label: "Preferences", group: "app" },
  { id: "about", label: "About", group: "app" },
];

function isTab(v: string | undefined): v is TabId {
  return (
    v === "vault" ||
    v === "security" ||
    v === "cache" ||
    v === "master" ||
    v === "vaults" ||
    v === "export" ||
    v === "preferences" ||
    v === "about"
  );
}

interface SectionBodyProps {
  readonly id: TabId;
  readonly onRotationStart: (currentPass: string, newPass: string) => void;
}

function SectionBody({ id, onRotationStart }: SectionBodyProps) {
  if (id === "vault") return <VaultDetailsTab />;
  if (id === "security") return <SecurityTab />;
  if (id === "cache") return <CacheTab />;
  if (id === "master")
    return <MasterPasswordTab onRotationStart={onRotationStart} />;
  if (id === "vaults") return <VaultsTab />;
  if (id === "export") return <ExportTab />;
  if (id === "preferences") return <PreferencesTab />;
  return <AboutTab />;
}

export function Settings() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { section } = useParams();
  const worker = useVault();
  const urlTab: TabId | null = isTab(section) ? section : null;
  const [rotationProgress, setRotationProgress] =
    useState<RotationProgress | null>(null);

  function onRotationStart(currentPass: string, newPass: string): void {
    setRotationProgress({
      phase: { kind: "verifying-current" },
      results: [],
    });
    void (async () => {
      try {
        await changeMaster(worker, currentPass, newPass, {
          onProgress: setRotationProgress,
        });
      } catch {
        // `changeMaster` already emitted an `aborted` phase via
        // `onProgress` before rethrowing — the modal renders the
        // failure surface from that. We swallow the rejection here
        // so it doesn't bubble up as an unhandled promise rejection.
      }
    })();
  }

  const rotationModal = rotationProgress && (
    <MasterRotationModal
      progress={rotationProgress}
      onClose={() => setRotationProgress(null)}
    />
  );

  if (isMobile) {
    if (!urlTab) {
      return (
        <>
          <div className="mx-auto max-w-md px-0 py-0">
            <header className="border-b border-border px-4 py-3 text-lg font-semibold">
              Settings
            </header>
            <SectionGroup label="This vault">
              {TABS.filter((t) => t.group === "vault").map((t) => (
                <SectionRow
                  key={t.id}
                  label={t.label}
                  onClick={() => navigate(`/settings/${t.id}`)}
                />
              ))}
            </SectionGroup>
            <SectionGroup label="App">
              {TABS.filter((t) => t.group === "app").map((t) => (
                <SectionRow
                  key={t.id}
                  label={t.label}
                  onClick={() => navigate(`/settings/${t.id}`)}
                />
              ))}
            </SectionGroup>
          </div>
          {rotationModal}
        </>
      );
    }
    return (
      <>
        <MobileWizardShell
          title={TABS.find((t) => t.id === urlTab)?.label ?? "Settings"}
          onBack={() => navigate("/settings")}
        >
          <SectionBody id={urlTab} onRotationStart={onRotationStart} />
        </MobileWizardShell>
        {rotationModal}
      </>
    );
  }

  // Desktop
  const activeTab: TabId = urlTab ?? "security";
  return (
    <>
      <div className="grid h-full min-h-[70vh] grid-cols-[200px_1fr]">
        <aside className="border-r border-border bg-bg-sidebar p-5 text-sm">
          <div className="mb-2 text-[10px] uppercase tracking-[0.14em] text-text-dim">
            This vault
          </div>
          <TabBtn
            id="vault"
            active={activeTab}
            onClick={(id) => navigate(`/settings/${id}`)}
          >
            Vault details
          </TabBtn>
          <TabBtn
            id="security"
            active={activeTab}
            onClick={(id) => navigate(`/settings/${id}`)}
          >
            Security
          </TabBtn>
          <TabBtn
            id="cache"
            active={activeTab}
            onClick={(id) => navigate(`/settings/${id}`)}
          >
            Cache
          </TabBtn>
          <div className="mt-4 text-[10px] uppercase tracking-[0.14em] text-text-dim">
            App
          </div>
          <TabBtn
            id="master"
            active={activeTab}
            onClick={(id) => navigate(`/settings/${id}`)}
          >
            Master password
          </TabBtn>
          <TabBtn
            id="vaults"
            active={activeTab}
            onClick={(id) => navigate(`/settings/${id}`)}
          >
            Vaults
          </TabBtn>
          <TabBtn
            id="export"
            active={activeTab}
            onClick={(id) => navigate(`/settings/${id}`)}
          >
            Export backup
          </TabBtn>
          <TabBtn
            id="preferences"
            active={activeTab}
            onClick={(id) => navigate(`/settings/${id}`)}
          >
            Preferences
          </TabBtn>
          <TabBtn
            id="about"
            active={activeTab}
            onClick={(id) => navigate(`/settings/${id}`)}
          >
            About
          </TabBtn>
        </aside>
        <div className="overflow-auto px-8 py-6">
          <SectionBody id={activeTab} onRotationStart={onRotationStart} />
        </div>
      </div>
      {rotationModal}
    </>
  );
}

function SectionGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <section className="px-0 pt-4">
      <div className="px-4 pb-2 text-[10px] font-medium uppercase tracking-[0.14em] text-text-dim">
        {label}
      </div>
      <div className="divide-y divide-border border-y border-border">
        {children}
      </div>
    </section>
  );
}

function SectionRow({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-14 w-full items-center gap-3 bg-bg-raised px-4 py-3 text-left text-base hover:bg-accent-bg"
    >
      <span className="flex-1">{label}</span>
      <ChevronRight className="h-4 w-4 text-text-dim" />
    </button>
  );
}

function TabBtn({
  id,
  active,
  onClick,
  children,
}: {
  id: TabId;
  active: TabId;
  onClick: (id: TabId) => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={() => onClick(id)}
      className={cn(
        "block w-full rounded px-2 py-1.5 text-left text-text-soft hover:bg-accent-bg",
        active === id && "bg-accent-bg text-text",
      )}
    >
      {children}
    </button>
  );
}
